import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useLang } from "@/contexts/LangContext";
import { ChevronRight, ChevronLeft, Search as SearchIcon } from "lucide-react";

const PAGE_SIZE = 48;

export default function Products() {
  const { t, lang } = useLang();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const cat = params.get("category") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/categories/counts").then((r) => setCounts(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (cat) qs.set("category_id", cat);
    if (q) qs.set("q", q);
    qs.set("sort", sort);
    qs.set("limit", String(PAGE_SIZE));
    qs.set("skip", String((page - 1) * PAGE_SIZE));
    api.get(`/products?${qs.toString()}`).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setLoading(false);
    });
  }, [cat, q, sort, page]);

  // Disable browser's built-in scroll restoration so OUR logic is authoritative on /products.
  useEffect(() => {
    const prev = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    return () => { if ("scrollRestoration" in window.history) window.history.scrollRestoration = prev; };
  }, []);

  // Persist the latest scrollY continuously while on this URL, throttled.
  // We use a debounce so transient auto-scrolls (Playwright, scroll-into-view, etc.) don't overwrite the user's real position.
  useEffect(() => {
    const key = `products-scroll:${location.pathname}${location.search}`;
    let timer = null;
    const persist = () => {
      const y = window.scrollY;
      if (y > 0) sessionStorage.setItem(key, String(y));
    };
    const onScroll = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(persist, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (timer) clearTimeout(timer);
    };
  }, [location.pathname, location.search]);

  // Restore on mount once items have loaded. Retries until the DOM is tall enough
  // to actually accommodate the saved scrollY (handles lazy-image height growth).
  useEffect(() => {
    if (loading || items.length === 0) return;
    const key = `products-scroll:${location.pathname}${location.search}`;
    const saved = sessionStorage.getItem(key);
    if (!saved) return;
    const y = parseInt(saved, 10);
    if (Number.isNaN(y) || y <= 0) return;

    let tries = 0;
    let cancelled = false;
    const tryRestore = () => {
      if (cancelled) return;
      window.scrollTo({ top: y, left: 0, behavior: "auto" });
      tries += 1;
      const close = Math.abs(window.scrollY - y) <= 50;
      if (!close && tries < 25) {
        setTimeout(tryRestore, 120);
      } else {
        sessionStorage.removeItem(key);
      }
    };
    requestAnimationFrame(tryRestore);
    return () => { cancelled = true; };
  }, [loading, items.length, location.pathname, location.search]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    // changing filters resets pagination + drops saved scroll for the new URL
    if (k !== "page") {
      next.delete("page");
    }
    setParams(next);
  };

  const goPage = (n) => {
    if (n < 1 || n > totalPages || n === page) return;
    setParam("page", n === 1 ? "" : String(n));
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  // Build context-aware sidebar: show the ROOT of the current category's tree
  const ctx = useMemo(() => {
    const byId = Object.fromEntries(categories.map((c) => [c.id, c]));
    // Walk up to find the root ancestor of the current category
    let cursor = byId[cat];
    const trail = [];
    while (cursor) { trail.unshift(cursor); cursor = cursor.parent_id ? byId[cursor.parent_id] : null; }
    const rootCat = trail[0] || null;
    const currentCat = trail[trail.length - 1] || null;
    // Children of root (level 1) + their children (level 2)
    const level1 = categories.filter((c) => c.parent_id === rootCat?.id).sort((a, b) => (a.order || 0) - (b.order || 0));
    return { rootCat, currentCat, trail, level1, byId };
  }, [categories, cat]);

  const headingCat = ctx.currentCat || ctx.rootCat;
  const headingName = headingCat ? (lang === "tr" ? headingCat.name_tr : headingCat.name_en) : (lang === "tr" ? "Mağaza" : "Shop");
  const rootName = ctx.rootCat ? (lang === "tr" ? ctx.rootCat.name_tr : ctx.rootCat.name_en) : (lang === "tr" ? "Mağaza" : "Shop");

  return (
    <div data-testid="products-page" className="px-6 lg:px-16 py-12 max-w-[1440px] mx-auto">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-[#C8A96A] mb-3">
          {ctx.trail.length > 1
            ? ctx.trail.slice(0, -1).map((c) => (lang === "tr" ? c.name_tr : c.name_en)).join(" / ")
            : (lang === "tr" ? "Mağaza" : "Shop")}
        </p>
        <h1 data-testid="products-heading" className="font-heading text-4xl lg:text-5xl">{headingName}</h1>
        <p className="text-sm text-[#52525B] mt-3">{total} {lang === "tr" ? "ürün" : "items"}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6 lg:gap-8">
        {/* Context-aware Sidebar */}
        <aside data-testid="filter-sidebar" className="space-y-8 lg:sticky lg:top-32 lg:self-start min-w-0 max-w-full">
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] mb-4 font-medium">{t("common.search")}</h3>
            <div className="relative">
              <SearchIcon size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input
                data-testid="search-input"
                type="text"
                defaultValue={q}
                onKeyDown={(e) => e.key === "Enter" && setParam("q", e.currentTarget.value)}
                placeholder={t("common.search")}
                className="w-full border-b border-[#0A0A0A] bg-transparent py-2 pl-6 text-sm focus:outline-none focus:border-[#C8A96A]"
              />
            </div>
          </div>

          {ctx.rootCat && (
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] mb-4 font-medium">{rootName}</h3>
              <ul className="space-y-1">
                <li>
                  <button data-testid="filter-cat-root" onClick={() => setParam("category", ctx.rootCat.id)}
                    className={`flex items-center justify-between w-full text-left text-sm py-1 ${cat === ctx.rootCat.id ? "text-[#C8A96A] font-medium" : "text-[#52525B] hover:text-[#0A0A0A]"}`}>
                    <span>{lang === "tr" ? "Tümü" : "All"}</span>
                    <span className="text-[10px] text-[#71717A]">{counts[ctx.rootCat.id] ?? 0}</span>
                  </button>
                </li>
                {ctx.level1.map((c) => {
                  const subs = categories.filter((s) => s.parent_id === c.id).sort((a, b) => (a.order || 0) - (b.order || 0));
                  const isActive = cat === c.id || subs.some((s) => s.id === cat);
                  return (
                    <li key={c.id}>
                      <button data-testid={`filter-cat-${c.slug}`} onClick={() => setParam("category", c.id)}
                        className={`flex items-center justify-between w-full text-left text-sm py-1 ${cat === c.id ? "text-[#C8A96A] font-medium" : isActive ? "text-[#0A0A0A] font-medium" : "text-[#52525B] hover:text-[#0A0A0A]"}`}>
                        <span className="flex items-center gap-1">
                          {subs.length > 0 && <ChevronRight size={12} className={isActive ? "rotate-90 transition-transform" : "transition-transform"} />}
                          {lang === "tr" ? c.name_tr : c.name_en}
                        </span>
                        <span className="text-[10px] text-[#71717A]">{counts[c.id] ?? 0}</span>
                      </button>
                      {isActive && subs.length > 0 && (
                        <ul className="ml-4 mt-1 space-y-0.5 border-l border-border pl-3">
                          {subs.map((s) => (
                            <li key={s.id}>
                              <button data-testid={`filter-sub-${s.slug}`} onClick={() => setParam("category", s.id)}
                                className={`flex items-center justify-between w-full text-left text-[13px] py-0.5 ${cat === s.id ? "text-[#C8A96A] font-medium" : "text-[#52525B] hover:text-[#0A0A0A]"}`}>
                                <span>{lang === "tr" ? s.name_tr : s.name_en}</span>
                                <span className="text-[10px] text-[#71717A]">{counts[s.id] ?? 0}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {!ctx.rootCat && (
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] mb-4 font-medium">{t("nav.categories")}</h3>
              <ul className="space-y-1">
                {categories.filter((c) => !c.parent_id).map((c) => (
                  <li key={c.id}>
                    <button data-testid={`filter-cat-${c.slug}`} onClick={() => setParam("category", c.id)}
                      className="flex items-center justify-between w-full text-left text-sm py-1 text-[#52525B] hover:text-[#0A0A0A]">
                      <span>{lang === "tr" ? c.name_tr : c.name_en}</span>
                      <span className="text-[10px] text-[#71717A]">{counts[c.id] ?? 0}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] mb-4 font-medium">{t("common.sort")}</h3>
            <select data-testid="sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)}
              className="w-full border-b border-[#0A0A0A] bg-transparent py-2 text-sm focus:outline-none focus:border-[#C8A96A]">
              <option value="newest">{lang === "tr" ? "En Yeni" : "Newest"}</option>
              <option value="price_asc">{lang === "tr" ? "Fiyat: Artan" : "Price: Low to High"}</option>
              <option value="price_desc">{lang === "tr" ? "Fiyat: Azalan" : "Price: High to Low"}</option>
              <option value="name">{lang === "tr" ? "İsim" : "Name"}</option>
            </select>
          </div>
        </aside>

        {/* Grid */}
        <div className="min-w-0">
          {loading ? (
            <div className="text-center py-20 text-sm text-[#52525B]">{t("common.loading")}</div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 text-sm text-[#52525B]">{lang === "tr" ? "Ürün bulunamadı" : "No products found"}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
                {items.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination data-testid="pagination" page={page} totalPages={totalPages} goPage={goPage} total={total} pageSize={PAGE_SIZE} lang={lang} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, goPage, total, pageSize, lang }) {
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  // Build a windowed page list: always show 1, last, current, and 1-2 neighbors
  const pages = [];
  const add = (n) => { if (n >= 1 && n <= totalPages && !pages.includes(n)) pages.push(n); };
  add(1); add(2);
  for (let d = -2; d <= 2; d++) add(page + d);
  add(totalPages - 1); add(totalPages);
  pages.sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < pages.length; i++) {
    out.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) out.push("…");
  }
  return (
    <nav data-testid="pagination" aria-label="Sayfalama" className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border pt-6">
      <p data-testid="pagination-summary" className="text-xs text-[#52525B] font-finance">
        {lang === "tr" ? `${start}–${end} arası, toplam ${total} ürün` : `${start}–${end} of ${total} products`}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-1 max-w-full">
        <button data-testid="pagination-prev" onClick={() => goPage(page - 1)} disabled={page <= 1}
          aria-label={lang === "tr" ? "Önceki" : "Previous"}
          className="px-2 sm:px-3 py-2 text-xs uppercase tracking-[0.18em] border border-border hover:bg-[#FAF8F3] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1">
          <ChevronLeft size={14} /> <span className="hidden sm:inline">{lang === "tr" ? "Önceki" : "Previous"}</span>
        </button>
        {out.map((it, idx) => it === "…" ? (
          <span key={`gap-${idx}`} className="px-2 text-xs text-[#71717A]">…</span>
        ) : (
          <button key={it} data-testid={`pagination-page-${it}`} onClick={() => goPage(it)}
            aria-current={it === page ? "page" : undefined}
            className={"min-w-[36px] h-9 text-xs font-finance border " + (it === page
              ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
              : "border-border hover:bg-[#FAF8F3]")}>
            {it}
          </button>
        ))}
        <button data-testid="pagination-next" onClick={() => goPage(page + 1)} disabled={page >= totalPages}
          aria-label={lang === "tr" ? "Sonraki" : "Next"}
          className="px-2 sm:px-3 py-2 text-xs uppercase tracking-[0.18em] border border-border hover:bg-[#FAF8F3] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1">
          <span className="hidden sm:inline">{lang === "tr" ? "Sonraki" : "Next"}</span> <ChevronRight size={14} />
        </button>
      </div>
    </nav>
  );
}
